import React from 'react';
import { getSellingPortfolio } from 'utils';
import {
  BM_PAYMENT_RESPONSE_STATUS,
  DashboardMetric,
  METRICS_LISTING_PAID_PACKAGE_LABEL,
} from '../types';
import { Link } from 'react-router-dom';
import { t } from 'i18next';
import PaymentStatus from '../components/PaymentStatus';
import { generateDaysLeftString } from 'dateUtils';

export const DashboardMetricColumns: Array<keyof DashboardMetric> = [
  'title',
  'stage',
  'paidPackage',
  'paymentStatus',
  'expiresOn',
  'publishedOn',
  'lastEdited',
  'numberOfEnquiries',
  'numberOfAccepted',
  'numberOfProspects',
  'numberOfBidders',
  'hasIM',
  'hasNDA',
  'dealRoom',
  'brokerName',
  'brokerageFirm',
];

const generateBooleanContent = (val: boolean) => (val ? t('yes') : t('no'));

export const DashboardMetricContentTransformations: Partial<
  Record<
    keyof DashboardMetric,
    (
      val: DashboardMetric,
      openInvoiceModal: (DashboardMetric: DashboardMetric) => void,
    ) => React.ReactNode
  >
> = {
  title: ({ title }) => <p className="as-link-black">{title}</p>,
  stage: ({ stage }) => t(stage),
  paidPackage: ({ paidPackage }) =>
    t(METRICS_LISTING_PAID_PACKAGE_LABEL[paidPackage]),
  expiresOn: ({ expiresOn }) => generateDaysLeftString(expiresOn),
  publishedOn: ({ publishedOn }) => generateDaysLeftString(publishedOn),
  lastEdited: ({ lastEdited }) => generateDaysLeftString(lastEdited),
  numberOfEnquiries: ({ numberOfEnquiries }) => numberOfEnquiries || 0,
  numberOfAccepted: ({ numberOfAccepted }) => numberOfAccepted || 0,
  numberOfProspects: ({ numberOfProspects }) => numberOfProspects || 0,
  numberOfBidders: ({ numberOfBidders }) => numberOfBidders || 0,
  hasIM: ({ hasIM }) => generateBooleanContent(hasIM),
  hasNDA: ({ hasNDA }) => generateBooleanContent(hasNDA),
  dealRoom: ({ dealRoom, bmPaymentResponse }) => {
    if (!dealRoom || bmPaymentResponse === BM_PAYMENT_RESPONSE_STATUS.REJECTED)
      return '';

    const { _id: dealRoomId } = dealRoom;

    return (
      <Link
        className="as-link-black"
        to={getSellingPortfolio(dealRoomId)}
        onClick={(e) => e.stopPropagation()}
      >
        {t('portfolio')}
      </Link>
    );
  },
  brokerName: ({ brokerName }) => brokerName || '',
  brokerageFirm: ({ brokerageFirm }) => brokerageFirm || '',
  paymentStatus: (dashboardMetric, openInvoiceModal) => (
    <PaymentStatus
      dashboardMetric={dashboardMetric}
      openInvoiceModal={openInvoiceModal}
    />
  ),
};

export const HeadingTranslations: Partial<
  Record<keyof DashboardMetric, string>
> = {
  title: 'title',
  stage: 'status',
  paidPackage: 'paidPackage',
  expiresOn: 'daysUntilPackExpires',
  publishedOn: 'publishedOn',
  lastEdited: 'lastEdited',
  numberOfEnquiries: 'numOfEnquiries',
  numberOfAccepted: 'numbOfAccepted',
  numberOfProspects: 'numOfProspects',
  numberOfBidders: 'numOfBidders',
  hasIM: 'imInDR',
  hasNDA: 'ndaAttached',
  dealRoom: 'portfolio',
  brokerName: 'broker',
  brokerageFirm: 'brokerageFirm',
  paymentStatus: 'payment',
};

export const DEAL_SORTING_VALUES: Partial<
  Record<keyof DashboardMetric, string>
> = {
  title: 'titleSort',
  stage: 'stage',
  paidPackage: 'paidPackage',
  expiresOn: 'expiresOn',
  publishedOn: 'publishedOn',
  lastEdited: 'lastEdited',
  numberOfEnquiries: 'numberOfEnquiries',
  numberOfAccepted: 'numberOfAccepted',
  numberOfProspects: 'numberOfProspects',
  numberOfBidders: 'numberOfBidders',
  hasIM: 'hasIM',
  hasNDA: 'hasNDA',
  brokerName: 'brokerNameSort',
  brokerageFirm: 'brokerageFirm',
  paymentStatus: 'paymentStatus',
};

export const CUSTOM_COL_SIZES: Partial<Record<keyof DashboardMetric, number>> =
  {
    title: 300,
    stage: 80,
    paidPackage: 100,
    publishedOn: 150,
    lastEdited: 150,
    numberOfEnquiries: 80,
    numberOfAccepted: 80,
    numberOfProspects: 80,
    numberOfBidders: 80,
    brokerName: 300,
    brokerageFirm: 300,
    paymentStatus: 300,
  };
